import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import axios from '../../axios';
import VoucherPrint from './VoucherPrint';
import {
  Container,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Card,
  FormControlLabel,
  Switch,
  Autocomplete
} from '@mui/material';
import { toast } from 'react-toastify';
import PaymentIcon from '@mui/icons-material/Payment';
import PrintIcon from '@mui/icons-material/Print';

export default function TaxPaymentForm() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get ID from URL
  const isView = window.location.pathname.includes('/view'); // Check if view mode
  const isEdit = !!id && !isView; // Check if edit mode

  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const branchId = parseInt(localStorage.getItem('branchId'), 10);
  const userId = parseInt(localStorage.getItem('userId'), 10);
  // Security Context
  const restrictedSafeId = localStorage.getItem("safeId") ? parseInt(localStorage.getItem("safeId"), 10) : null;
  const allowedSafeIds = localStorage.getItem("allowedSafeIds") ? JSON.parse(localStorage.getItem("allowedSafeIds")) : [];

  const [form, setForm] = useState({
    companyId,
    branchId,
    voucherNumber: '',
    voucherDate: new Date().toLocaleDateString('en-CA'),
    amount: '',
    description: 'سند صرف ضريبة برقم',
    supplierId: '',
    paymentType: 0, // 0 = Cash, 1 = Bank
    safeId: '',
    bankId: '',
    userId,
    referenceNumber: '',
    referenceDate: new Date().toLocaleDateString('en-CA'),
    // حقول الضريبة
    isTax: 1,
    vatTypeId: '',
    // عرض فقط: قيمة الضريبة المحسوبة (لا تُرسل)
    TaxAmount: '',
    journalEntryGlobalId: null
  });

  const [safes, setSafes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [vatTypes, setVatTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [nextVoucherNumber, setNextVoucherNumber] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  
  const printRef = useRef();

  const handlePrint = useReactToPrint({
      contentRef: printRef,
      documentTitle: `Voucher-${form.voucherNumber || 'New'}`,
  });

  // Prepare data for print
  const voucherForPrint = {
      ...form,
      voucherType: 105, // Tax Payment
      supplierName: suppliers.find(s => s.id === form.supplierId)?.name || '',
      safeName: safes.find(s => s.safeId === form.safeId)?.name || '',
      bankName: banks.find(b => b.id === form.bankId)?.name || '',
      voucherNumber: id ? form.voucherNumber : nextVoucherNumber
  };

  useEffect(() => {
    if (!companyId || !branchId || id) return;

    const loadNextVoucherNumber = async () => {
      try {
        const response = await axios.get(`/Vouchers/next-number/${companyId}/${branchId}/105`);
        setNextVoucherNumber(response.data.nextNumber || response.data.toString());
      } catch (err) {
        console.error('Error loading next voucher number:', err);
        setNextVoucherNumber('1');
      }
    };

    loadNextVoucherNumber();
  }, [companyId, branchId, id]);

  useEffect(() => {
    if (!companyId) return;
    const loadData = async () => {
      setLoading(true);
      try {
        const [safesRes, banksRes, vatRes, suppliersRes, companyRes] = await Promise.all([
          axios.get(`/Safe/by-company/${companyId}`),
          axios.get(`/Bank/by-company/${companyId}`),
          axios.get(`/VatTypes`, { params: { companyId } }),
          axios.get(`/Supplier/by-company/${companyId}`),
          axios.get(`/CompanyProfile/public/${companyId}`)
        ]);
        setSafes(safesRes.data || []);
        setBanks(banksRes.data || []);
        setVatTypes(vatRes.data || []); 
        setSuppliers(suppliersRes.data || []);
        setCompanyInfo(companyRes.data);

        // Load Voucher Data if ID exists
        if (id) {
             const res = await axios.get(`/Vouchers/${id}`);
             const v = res.data;
             
             setForm(prev => ({
                 ...prev,
                 voucherNumber: v.voucherNumber,
                 voucherDate: v.voucherDate.split('T')[0],
                 amount: v.amount,
                 description: v.description,
                 supplierId: v.supplierId,
                 paymentType: v.paymentType === 1 ? 0 : 1, // API: 1=Cash, 2=Bank. Form: 0=Cash, 1=Bank
                 safeId: v.safeId || '',
                 bankId: v.bankId || '',
                 referenceNumber: v.referenceNumber || '',
                 referenceDate: v.referenceDate ? v.referenceDate.split('T')[0] : '',
                 isTax: 1,
                 vatTypeId: v.vatTypeId,
                 TaxAmount: v.taxAmount,
                 journalEntryGlobalId: v.journalEntryGlobalId
             }));
             setNextVoucherNumber(v.voucherNumber);
        }

      } catch (err) {
        console.error('Error loading data:', err);
        setError('فشل في تحميل البيانات');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [companyId, id]);

  // حساب الضريبة تلقائياً من المبلغ ونوع الضريبة
  useEffect(() => {
    // إذا لم يتم إدخال المبلغ أو اختيار نوع الضريبة، لا نحسب
    const amt = parseFloat(form.amount);
    const vatId = form.vatTypeId ? parseInt(form.vatTypeId, 10) : null;
    const vat = vatTypes.find(v => v.id === vatId);

    if (!vat || !isFinite(amt) || amt <= 0) {
      if (form.TaxAmount !== '') {
        setForm(prev => ({ ...prev, TaxAmount: '' }));
      }
      return;
    }

    const rateRaw = Number(vat.rate) || 0;
    // تطبيع النسبة: إن كانت ككسر (0.15) نضربها بـ 100 → 15%
    const effectiveRate = rateRaw <= 1 ? rateRaw * 100 : rateRaw;
    
    let tax = 0;
    if (form.priceIncludesTax) {
      // إذا كان السعر شامل الضريبة:
      // Tax = Amount - (Amount / (1 + Rate))
      const amountBeforeTax = amt / (1 + (effectiveRate / 100));
      tax = amt - amountBeforeTax;
    } else {
      // الضريبة على مبلغ غير شامل: amount * rate%
      tax = amt * (effectiveRate / 100);
    }

    // Round to 2 decimal places
    const taxStr = (Math.round(tax * 100) / 100).toFixed(2);

    if (form.TaxAmount !== taxStr) {
      setForm(prev => ({ ...prev, TaxAmount: taxStr }));
    }
  }, [form.amount, form.vatTypeId, form.priceIncludesTax, vatTypes]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handlePriceIncludesTaxChange = (e) => {
      // عند تغيير خيار "شامل الضريبة"، نحتاج فقط لتحديث الحالة، والـ useEffect سيقوم بإعادة الحساب
      // لكن، هل نريد تغيير "المبلغ"؟ 
      // السيناريو: المستخدم أدخل 115 وقال "شامل". الضريبة تحسب 15. المبلغ المرسل للباك اند هو 100؟ 
      // عادة في سندات الصرف، المبلغ هو المبلغ المدفوع (أي الـ 115).
      // لكن محاسبياً، القيد يكون: 
      // من مذكورين:
      //   ح/ المورد (أو مصروف) 100
      //   ح/ ضريبة المدخلات 15
      // إلى ح/ الصندوق 115
      
      // في هذا الفورم، نحن نرسل `amount`. الباك اند يتوقع المبلغ الصافي أم الإجمالي؟
      // في الكود الحالي للباك اند (غير مرئي لي الآن لكن استنتج من السياق)، عادة يرسل المبلغ الصافي ويحسب الضريبة عليه، أو يرسل الإجمالي.
      // لكن بما أن المستخدم طلب "يحسب المبلغ قبل الضريبة"، فهذا يعني أنه يريد رؤية المبلغ قبل الضريبة أو أن يتم اعتماد المبلغ قبل الضريبة.
      
      // الملاحظة في الـ UI: "المبلغ (الأساسي)" و "قيمة الضريبة (تقريبي)".
      // إذا كان شامل، فالمبلغ المدخل هو الإجمالي.
      
      // سنقوم فقط بتحديث الحالة هنا.
      setForm(prev => ({ ...prev, priceIncludesTax: e.target.checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isView) return;

    // Validation
    if (!form.supplierId) {
      setError('يرجى اختيار المورد');
      return;
    }

    const amountVal = parseFloat(form.amount);
    if (!form.amount || isNaN(amountVal) || amountVal <= 0) {
      setError('يرجى إدخال مبلغ صحيح');
      return;
    }

    if (form.paymentType === 0 && !form.safeId) {
      setError('يرجى اختيار الخزنة للدفع النقدي');
      return;
    }
    if (form.paymentType === 1 && !form.bankId) {
      setError('يرجى اختيار البنك للدفع البنكي');
      return;
    }

    // التحقق من الضريبة: يتطلب تحديد نوع الضريبة
    if (form.isTax === 1 && !form.vatTypeId) {
      setError('يرجى اختيار نوع الضريبة');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Calculate Amount
      let finalAmount = amountVal;
      if (form.priceIncludesTax && form.vatTypeId) {
          const vat = vatTypes.find(v => v.id === form.vatTypeId);
          if (vat) {
              const rateRaw = Number(vat.rate) || 0;
              // Normalize rate: if 0.15 -> 15, if 15 -> 15
              const effectiveRate = rateRaw <= 1 ? rateRaw * 100 : rateRaw;
              // Amount = Total / (1 + rate%)
              finalAmount = amountVal / (1 + (effectiveRate / 100));
          }
      }

      const payload = {
        id: id ? parseInt(id) : 0,
        companyId: form.companyId,
        branchId: form.branchId,
        voucherType: 105, // GeneralPayment
        paymentType: form.paymentType === 0 ? 1 : 2, // Cash = 1, Bank = 2
        amount: finalAmount,
        supplierId: parseInt(form.supplierId),
        voucherDate: form.voucherDate + 'T12:00:00.000Z',
        description: form.description || '',
        notes: form.description || '',
        safeId: form.paymentType === 0 ? parseInt(form.safeId) : null,
        bankId: form.paymentType === 1 ? parseInt(form.bankId) : null,
        userId: form.userId,
        createdBy: localStorage.getItem('userName') || 'System',
        referenceNumber: form.referenceNumber || null,
        referenceDate: form.referenceDate ? form.referenceDate + 'T12:00:00.000Z' : null,
        // إرسال حقول الضريبة
        isTax: 1,
        vatTypeId: form.vatTypeId ? parseInt(form.vatTypeId, 10) : null
      };

      if (id) {
          // Update
          await axios.put(`/Vouchers/${id}`, payload);
          toast.success('تم تحديث سند صرف الضريبة بنجاح');
      } else {
          // Create
          await axios.post('/Vouchers/Tax-payment', payload);
          toast.success('تم إنشاء سند صرف الضريبة بنجاح');
      }
      navigate('/vouchers-list');
    } catch (err) {
      console.error('Error creating/updating tax payment voucher:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء حفظ السند');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }} dir="rtl">
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ bgcolor: 'warning.main', color: 'white', p: 3, display: 'flex', alignItems: 'center' }}>
          <PaymentIcon sx={{ fontSize: 40, mr: 2, color: 'white' }} />
          <Typography variant="h4" fontWeight="bold" color="inherit">
            سند صرف مع ضريبة
          </Typography>
          <Box flexGrow={1} />
          <Chip 
            label={`# ${id ? form.voucherNumber : nextVoucherNumber}`} 
            sx={{ 
              bgcolor: 'white', 
              color: 'warning.main', 
              fontSize: '1.2rem', 
              fontWeight: 'bold',
              px: 2
            }} 
          />
        </Box>

        {/* Hidden Print Component */}
        <div style={{ display: 'none' }}>
            <VoucherPrint 
                ref={printRef} 
                voucher={voucherForPrint}
                companyInfo={companyInfo}
            />
        </div>

        <Box p={4}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Top Section: Voucher Info */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="رقم السند"
                  value={id ? form.voucherNumber : nextVoucherNumber}
                  disabled
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="تاريخ السند"
                  type="date"
                  name="voucherDate"
                  value={form.voucherDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  disabled={isView}
                />
              </Grid>
            </Grid>

            {/* Middle Section: Financials and Payment */}
            <Card variant="outlined" sx={{ p: 3, mb: 4, bgcolor: '#fff', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                � البيانات المالية والدفع
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                {/* Row 1: Supplier, Payment Type, Safe/Bank */}
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        options={suppliers}
                        getOptionLabel={(option) => option.name || ''}
                        value={suppliers.find(s => s.id === form.supplierId) || null}
                        onChange={(e, newValue) => {
                            setForm(prev => ({ ...prev, supplierId: newValue ? newValue.id : '' }));
                        }}
                        disabled={isView}
                        renderInput={(params) => <TextField {...params} label="المورد (الجهة الضريبية)" required />}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth required>
                        <InputLabel>نوع الدفع</InputLabel>
                        <Select
                            name="paymentType"
                            value={form.paymentType}
                            onChange={handleChange}
                            label="نوع الدفع"
                            disabled={isView}
                        >
                            <MenuItem value={0}>💵 نقدي</MenuItem>
                            <MenuItem value={1}>🏦 بنكي</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                    {form.paymentType === 0 ? (
                        <Autocomplete
                            options={safes}
                            getOptionLabel={(option) => option.name || ''}
                            value={safes.find(s => s.safeId === form.safeId) || null}
                            onChange={(e, newValue) => {
                                setForm(prev => ({ ...prev, safeId: newValue ? newValue.safeId : '' }));
                            }}
                            disabled={!!restrictedSafeId || isView}
                            renderInput={(params) => <TextField {...params} label="الصندوق" required />}
                        />
                    ) : (
                        <Autocomplete
                            options={banks}
                            getOptionLabel={(option) => option.name || ''}
                            value={banks.find(b => b.id === form.bankId) || null}
                            onChange={(e, newValue) => {
                                setForm(prev => ({ ...prev, bankId: newValue ? newValue.id : '' }));
                            }}
                            disabled={isView}
                            renderInput={(params) => <TextField {...params} label="البنك" required />}
                        />
                    )}
                </Grid>

                {/* Row 2: Reference Info */}
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="رقم المرجع"
                        name="referenceNumber"
                        value={form.referenceNumber}
                        onChange={handleChange}
                        disabled={isView}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="تاريخ المرجع"
                        type="date"
                        name="referenceDate"
                        value={form.referenceDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        disabled={isView}
                    />
                </Grid>

                {/* Divider */}
                <Grid item xs={12}>
                    <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                </Grid>

                {/* Row 3: Tax & Amount Info */}
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth required>
                        <InputLabel>نوع الضريبة</InputLabel>
                        <Select
                            name="vatTypeId"
                            value={form.vatTypeId}
                            onChange={handleChange}
                            label="نوع الضريبة"
                            disabled={isView}
                        >
                            {vatTypes.map(vt => {
                                const rateNum = Number(vt.rate);
                                const effectiveRate = rateNum <= 1 ? rateNum * 100 : rateNum;
                                return (
                                    <MenuItem key={vt.id} value={vt.id}>
                                        {vt.name} ({effectiveRate.toFixed(2)}%)
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField
                        fullWidth
                        size="small"
                        label="المبلغ"
                        name="amount"
                        type="number"
                        value={form.amount}
                        onChange={handleChange}
                        required
                        inputProps={{ min: 0, step: 0.01 }}
                        disabled={isView}
                        sx={{ fontWeight: 'bold' }}
                    />
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.priceIncludesTax}
                                onChange={handlePriceIncludesTaxChange}
                                name="priceIncludesTax"
                                color="primary"
                                disabled={isView}
                            />
                        }
                        label="شامل الضريبة"
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField
                        fullWidth
                        size="small"
                        label="قيمة الضريبة"
                        value={form.TaxAmount}
                        disabled
                        sx={{ 
                            '& input': { color: 'primary.main', fontWeight: 'bold' } 
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        size="small"
                        label="المبلغ قبل الضريبة"
                        value={form.priceIncludesTax ? ((parseFloat(form.amount) || 0) - (parseFloat(form.TaxAmount) || 0)).toFixed(2) : (parseFloat(form.amount) || 0).toFixed(2)}
                        disabled
                        sx={{ fontWeight: 'bold' }}
                    />
                </Grid>
              </Grid>
            </Card>

            {/* Bottom Section: Description */}
            <Box mt={4}>
              <Typography variant="h6" gutterBottom color="text.secondary" fontWeight="bold">
                📝 البيان / الملاحظات
              </Typography>
              <TextField
                fullWidth
                name="description"
                value={form.description}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="اكتب وصفاً تفصيلياً للسند..."
                disabled={isView}
                sx={{ 
                  bgcolor: '#f5f5f5',
                  '& .MuiOutlinedInput-root': { fontSize: '1.1rem' } 
                }}
              />
            </Box>

            {/* Actions */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={4} pt={2} borderTop={1} borderColor="divider">
              {form.journalEntryGlobalId && (
                <Button 
                  variant="outlined" 
                  color="secondary"
                  size="large"
                  onClick={() => window.open(`/journal-entries/${form.journalEntryGlobalId}/view`, "_blank")}
                >
                  👁️ عرض القيد المحاسبي
                </Button>
              )}
              <Button
                  variant="outlined"
                  color="info"
                  size="large"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
              >
                  طباعة
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                onClick={() => navigate('/vouchers-list')}
                disabled={submitting}
              >
                {isView ? 'رجوع' : 'إلغاء'}
              </Button>
              {!isView && (
                <Button
                  type="submit"
                  variant="contained"
                  color="warning"
                  size="large"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <PaymentIcon />}
                  sx={{ px: 4, py: 1, fontSize: '1.1rem' }}
                >
                  {submitting ? 'جاري الحفظ...' : (id ? 'تحديث السند' : 'حفظ السند')}
                </Button>
              )}
            </Box>
          </form>
        </Box>
      </Paper>
    </Container>
  );
}
